import {carsRentStore, persistentStore} from '@/composables/store-accessor'
import {filters, searchRequest, searchResponse} from '@/utils/cars/cars-rent-blank-state'
import {
    B2B_AUTH_EVENT,
    CHANGE_LOCALE_EVENT,
    CHANGE_PRICE_EVENT,
    EventBus,
    FILTER_EVENT,
    RESET as RESET_EVENT,
    RESET_BOOKING_DATA,
    SEARCH_EVENT,
} from '@/utils/event-bus'
import {appInstance} from '@/utils/app-accessor'
import {isEqual} from '@/utils/helpers'
import {search as searchFn} from '@/utils/store-helpers'
import type {Ref} from 'vue'
import {useProductRuntimeStore} from '~/composables/product-runtime-store'

const runtimeFiltersBlank = () => ({})

async function loadCity(point: any, id: any) {
    try {
        const {
            cities: [city],
        } = await appInstance.$api.locations.get({id, limitCities: 1})
        return {[point]: city}
    } catch (e) {
        return {[point]: {}}
    }
}

export const useCarsRentRuntimeStore = defineStore('carsRentRuntime', () => {
    const productRuntimeStore = useProductRuntimeStore()
    const {
        SET_PARTIAL_SEARCH_RESULTS_CTS,
        searchActiveCount,
        SET_OFFERS,
        START_SEARCH,
        STOP_SEARCH,
        START_FILTER,
        STOP_FILTER,
        stopSearch,
        filterActive,
    } = productRuntimeStore
    const pickUpPoint: Ref = ref({})
    const dropOffPoint: Ref = ref({})
    const runtimeFilters = ref(runtimeFiltersBlank())

    function _newSearch(searchRequest: any) {
        stopSearch()
        SET_OFFERS([])
        SET_RUNTIME_FILTERS(runtimeFiltersBlank())
        carsRentStore.SET_SEARCH_RESPONSE(searchResponse())
        carsRentStore.NEW_SEARCH(searchRequest)
    }

    function SET_RUNTIME_FILTERS(filters: any) {
        runtimeFilters.value = filters
    }

    function SET_PICK_UP_POINT(val: any) {
        pickUpPoint.value = val
    }

    function SET_DROP_OFF_POINT(val: any) {
        dropOffPoint.value = val
    }

    function RESET() {
        searchActiveCount.value = 0
    }

    async function loadPickUpPoint(cityId: any) {
        pickUpPoint.value = ((await loadCity('pickUpPoint', cityId)) as any).pickUpPoint
    }

    async function loadDropOffPoint(cityId: any) {
        dropOffPoint.value = ((await loadCity('dropOffPoint', cityId)) as any).dropOffPoint
    }

    function clientInit() {
        EventBus.$on(RESET_EVENT, reset)
        EventBus.$on(B2B_AUTH_EVENT, reset)
        EventBus.$on(RESET_BOOKING_DATA, reset)
        EventBus.$on(CHANGE_LOCALE_EVENT, reload)
        EventBus.$on(CHANGE_PRICE_EVENT, changePrice)
        appInstance.$workers.carsRentWorker.onmessage = ({data}: any) => {
            if (data === 'load') {
                if (isEqual({...filters(), price: carsRentStore.searchResponse.filters.price}, carsRentStore.filters)) {
                    sort()
                } else {
                    filter()
                }
                return
            } else if (data === 'refresh') {
                return
            }
            STOP_FILTER()
            if (filterActive.value) return
            SET_OFFERS(data)
            EventBus.$emit(FILTER_EVENT)
        }
    }

    async function changePrice({offerKey, prepareBookResponse}: any) {
        carsRentStore.REFRESH_BASKET_PRICE({offerKey, prepareBookResponse})
        persistentStore.REFRESH_CONDITIONS({offerKey, prepareBookResponse})
    }

    function filter() {
        START_FILTER()
        appInstance.$workers.carsRentWorker.postMessage({
            action: 'filter',
            filters: {...carsRentStore.filters, ...runtimeFilters.value},
            sortKey: carsRentStore.sortFnName,
        })
    }

    function sort() {
        START_FILTER()
        appInstance.$workers.carsRentWorker.postMessage({action: 'sort', sortKey: carsRentStore.sortFnName})
    }

    function reset() {
        _newSearch(searchRequest())
        RESET()
        carsRentStore.RESET()
    }

    function load(offers = []) {
        appInstance.$workers.carsRentWorker.postMessage({action: 'load', offers})
    }

    async function reload() {
        const promises = []
        if (pickUpPoint.value.id) promises.push(loadPickUpPoint(pickUpPoint.value.id))
        if (dropOffPoint.value.id) promises.push(loadDropOffPoint(dropOffPoint.value.id))
        await Promise.all(promises)
    }

    async function search(rq: any) {
        START_SEARCH()
        _newSearch(rq)
        EventBus.$emit(SEARCH_EVENT)
        try {
            await searchFn(
                SET_PARTIAL_SEARCH_RESULTS_CTS,
                rq,
                'carsRent',
                carsRentStore,
                appInstance.$api.searchCarRent,
                'products'
            )
        } finally {
            STOP_SEARCH()
        }
    }

    function newSearch() {
        _newSearch(searchRequest())
    }

    const searchPageLink = computed(() => {
        return (searchRequest: any) => {
            const {partialResponse, convertToCurrency, ...query} = searchRequest
            return {name: 'carsRent', query}
        }
    })

    const offerMainImage = computed(() => {
        return (offer: any, info: any) => {
            if (!info.images || !info.images.length) return {url: null}
            let mainImage = info.images.find((image: any) => image.mainImage) || {url: null}
            if (!mainImage.url && info.images.length > 0) {
                mainImage = info.images[0]
            }
            return mainImage
        }
    })

    return {
        ...productRuntimeStore,
        pickUpPoint,
        dropOffPoint,
        runtimeFilters,
        SET_RUNTIME_FILTERS,
        SET_PICK_UP_POINT,
        SET_DROP_OFF_POINT,
        RESET,
        searchActiveCount,
        loadPickUpPoint,
        loadDropOffPoint,
        clientInit,
        changePrice,
        filter,
        sort,
        reset,
        load,
        reload,
        search,
        newSearch,
        searchPageLink,
        offerMainImage,
    }
})

// TODO [P3] Need check this HRM improvement. Relates with https://pinia.vuejs.org/cookbook/hot-module-replacement.html#HMR-Hot-Module-Replacement-
if (import.meta.hot) import.meta.hot.accept(acceptHMRUpdate(useCarsRentRuntimeStore, import.meta.hot))
